<script setup lang="ts">
import type { Report } from '@functions/types/analysis';
import type { Session } from '@functions/types/session';
import { subDays } from 'date-fns';
import { collection, doc, limit, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { computed, ref } from 'vue';
import { useCollection, useDocument } from 'vuefire';
import { useDebugUser } from '@/composables/useDebugUser';
import Container from '@/layouts/Container.vue';
import KeyMetricsGrid from '../components/trends/KeyMetricsGrid.vue';
import PerformanceInsights from '../components/trends/PerformanceInsights.vue';

import PerformanceTrends from '../components/trends/PerformanceTrends.vue';
import ShotPatternAnalysis from '../components/trends/ShotPatternAnalysis.vue';
// Import components
import TimeRangeSelector from '../components/trends/TimeRangeSelector.vue';
import { db } from '../firebase';
import { calculateMetrics, calculateShotPatterns, generateInsights, generateKeyMetrics } from '../utils/trendsMetricsCalculator';

definePage({
  name: 'trends',
});

const user = useDebugUser();
const selectedRange = ref('180d');

// Computed query based on selected time range
const sessionsQuery = computed(() => {
  if (!user.value?.uid) return null;

  const collectionRef = collection(db, 'imports', user.value.uid, 'sessions');
  const constraints = [];

  // Add date range filter
  if (selectedRange.value !== 'all') {
    const daysAgo = Number.parseInt(selectedRange.value);
    const date = subDays(new Date(), daysAgo);
    constraints.push(where('recordedAt', '>=', Timestamp.fromDate(date)));
  }

  constraints.push(orderBy('recordedAt', 'desc'));
  constraints.push(limit(100));

  return query(collectionRef, ...constraints);
});
const { data: sessions, pending } = useCollection<Session>(sessionsQuery);

const reportsQuery = computed(() => {
  if (!user.value?.uid) return null;

  const collectionRef = collection(db, 'imports', user.value.uid, 'reports');
  const constraints = [];

  // Add date range filter
  if (selectedRange.value !== 'all') {
    const daysAgo = Number.parseInt(selectedRange.value);
    const date = subDays(new Date(), daysAgo);
    constraints.push(where('recordedAt', '>=', Timestamp.fromDate(date)));
  }

  constraints.push(orderBy('recordedAt', 'desc'));
  return query(collectionRef, ...constraints);
});

// Fetch sessions using VueFire
const { data: reports } = useCollection<Report>(reportsQuery);

// Fetch user profile data
const userProfileQuery = computed(() => {
  if (!user.value?.uid) return null;
  return doc(db, 'users', user.value.uid);
});

const { data: userProfile } = useDocument(userProfileQuery);

// Show message when no data is found
const noDataMessage = computed(() => {
  if (pending.value) return '';
  if (!sessions.value?.length) {
    const timeRangeMap: Record<string, string> = {
      '30d': 'last 30 days',
      '90d': 'last 90 days',
      '180d': 'last 180 days',
      'all': 'all time',
    };
    const timeFrame = timeRangeMap[selectedRange.value] || 'selected time frame';
    return `No practice sessions found in the ${
      timeFrame === 'all time' ? 'database' : timeFrame
    }. Select a different range above to see your trends!`;
  }
  return '';
});

// Computed metrics based on sessions data
const computedMetrics = computed(() => {
  if (!sessions.value?.length || !reports.value?.length) {
    return calculateMetrics([], []);
  }
  return calculateMetrics(sessions.value, reports.value);
});

// Update keyMetrics to use computed values and handicap benchmarks
const keyMetrics = computed(() => {
  const userHandicap = userProfile.value?.handicap ?? null;
  return generateKeyMetrics(computedMetrics.value, userHandicap);
});

// Calculate insights based on recent performance
const performanceInsights = computed(() => {
  if (!sessions.value?.length) return [];
  return generateInsights(computedMetrics.value, sessions.value);
});

// Calculate shot pattern statistics
const shotPatterns = computed(() => {
  if (!sessions.value?.length) return null;
  return calculateShotPatterns(sessions.value);
});
</script>

<template>
  <Container>
    <template #title>
      Performance Trends
    </template>

    <div class="flex items-center justify-between mb-8 flex-wrap gap-4 border-b border-gray-200 pb-4 overflow-x-auto">
      <!-- Time Range Selector -->
      <TimeRangeSelector v-model="selectedRange" />
    </div>
    <p v-if="reports?.length <= 5" class="text-gray-600 dark:text-gray-400 text-sm mb-2">
      You have only {{ reports?.length }} session imported. Import more data to see more meaningful trends.
    </p>

    <!-- Loading State -->
    <div v-if="pending" class="space-y-6">
      <div class="text-center py-8 px-4">
        <i class="pi pi-spin pi-spinner text-2xl" />
        <p class="mt-2 text-gray-600">
          Loading...
        </p>
      </div>
    </div>

    <!-- No Data Message -->
    <div v-else-if="noDataMessage" class="text-center py-8 px-4">
      <p class="text-gray-600 dark:text-gray-400 text-lg">
        {{ noDataMessage }}
      </p>
    </div>

    <!-- Content -->
    <div v-else class="border shadow-lg rounded-lg p-4 border-gray-200">
      <!-- Key Metrics Grid with Benchmarks -->
      <KeyMetricsGrid :metrics="keyMetrics" :loading="pending" />

      <!-- Performance Insights -->
      <PerformanceInsights :insights="performanceInsights" />

      <!-- Shot Pattern Analysis -->
      <ShotPatternAnalysis :shot-patterns="shotPatterns" />

      <!-- Charts Section -->
      <PerformanceTrends :reports="reports" :loading="pending" />
    </div>
  </Container>
</template>

<style>
.trends-container {
  max-width: 1400px;
  margin: 0 auto;
}
.p-togglebutton-content {
  white-space: nowrap;
}
</style>
